import { DEFAULT_LOCALE } from './locales';

/**
 * Formatacao de data, hora e numero no locale **e no fuso da pessoa**.
 *
 * A §2.2 e explicita: armazenar em UTC, exibir no fuso do usuario, guardar o
 * fuso como identificador IANA. Nenhuma tela chama `toLocaleString()` sem
 * passar o fuso, porque o default e o fuso do navegador — que e o fuso de quem
 * olha, e nao o de quem tem a aula. Uma aula das 23h em Nova York apareceria no
 * dia seguinte para quem abrisse a tela em Sao Paulo.
 */

export type Instant = string | number | Date;

function toDate(instant: Instant): Date {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`instante invalido: ${String(instant)}`);
  }
  return date;
}

const DEFAULT_DATE_TIME: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

export function formatInPersonTimezone(
  instantUTC: Instant,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_TIME,
): string {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: timezone }).format(
    toDate(instantUTC),
  );
}

export function formatDateInPersonTimezone(
  instantUTC: Instant,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): string {
  return formatInPersonTimezone(instantUTC, timezone, locale, { dateStyle: 'medium' });
}

export function formatTimeInPersonTimezone(
  instantUTC: Instant,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): string {
  return formatInPersonTimezone(instantUTC, timezone, locale, { timeStyle: 'short' });
}

/**
 * Partes da data ja no fuso da pessoa. Serve para comparar dia, e nao para
 * exibir: comparar strings formatadas seria comparar tambem o idioma.
 */
export function partsInPersonTimezone(
  instantUTC: Instant,
  timezone: string,
): { year: string; month: string; day: string; hour: string; minute: string } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = new Map(
    formatter.formatToParts(toDate(instantUTC)).map((part) => [part.type, part.value]),
  );

  return {
    year: parts.get('year') ?? '',
    month: parts.get('month') ?? '',
    day: parts.get('day') ?? '',
    hour: parts.get('hour') ?? '',
    minute: parts.get('minute') ?? '',
  };
}

export function formatNumber(
  value: number,
  locale: string = DEFAULT_LOCALE,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}
